import { defineComponent, h, nextTick } from 'vue';
import { QBtn, QCheckbox, QIcon } from 'quasar';
import PropertyGrid from '../../../form/PropertyGrid2.jsx';
import SchemaForm from '../../../schemaform/SchemaForm.jsx';
import UserControl from '../../../UserControl.jsx';
import { PlainWrapper } from '@/Shared/helperClasses';
import { buildEssentialIconForm, buildMatchPortsFormItems } from './matchPorts.js';
import {
    listEssentialArgumentEditors,
    shouldUseMixedEssentialStack,
    getCaptionEditorDescriptor,
    getOutputEditorDescriptor,
} from './customFormModel.js';
import { VariableBindingProvider } from '../../../schemaform/VariableBindableField.jsx';
import { FlowContextProvider } from '../../../schemaform/FlowContextProvider.jsx';
import {
    getMixedStackArgCompilerError,
    getCustomFormCompilerErrorBanner,
    errorsMapForCustomForm,
    getCompileErrorBag,
    syncCompileErrorErrorsMap,
} from '../../../schemaform/essentialsCompilerError.js';
import { BREAK_ACCENT_COLOR } from './statusTooltip.js';
import { shouldStopOverlayPress } from './iconOverlayDrag.js';

const BREAKPOINT_RAIL_TITLE = 'Breakpoint — debugging will pause here.';

/** Default popup width in paper-local px (user can resize; CSS transform matches graph zoom). */
const OVERLAY_WIDTH_PX = 300;
const OVERLAY_MIN_WIDTH_PX = 220;
const OVERLAY_HEIGHT_ESSENTIAL_MIN_PX = 80;
const OVERLAY_HEIGHT_ADVANCED_PX = 340;
const OVERLAY_MIN_HEIGHT = 140;
/** Essentials form content scale (popup chrome/size stays at full graph zoom). */
const ESSENTIAL_FORM_RELATIVE_SCALE = 0.8;

const sharedFormComponents = {
    QBtn, QCheckbox, QIcon,
    PropertyGrid, SchemaForm, UserControl, VariableBindingProvider, FlowContextProvider,
};

const sharedFormProps = {
    advanced: { type: Boolean, default: false },
    pinned: { type: Boolean, default: false },
    schema: { type: Object, default: null },
    model: { type: Object, default: null },
    tool: { type: Object, default: null },
    root: { default: null },
    scopeitems: { default: null },
    controlscope: { default: null },
    /** Prefix for child control names (avoids duplicate names when both surfaces exist briefly). */
    namePrefix: { type: String, default: 'iconOverlay' },
    /** Remount custom form when selection changes (UserControl only reads controlData in created). */
    selectionKey: { type: String, default: '' },
    /**
     * FlowV2 variable binding context (createVariableBindingContext).
     * Provided to SchemaForm + UserControl subtrees when essentials are shown.
     */
    variableBindingContext: { type: Object, default: null },
    /**
     * FlowV2 designer context (createFlowDesignerContext).
     * Channel / project / method identity for schema conditions and LookupURL.
     */
    flowContext: { type: Object, default: null },
    /**
     * Selected icon ValidationError map (prop.Errors), keyed by ArgName.
     * Used to sync CompileError bags for customForm UserControls.
     */
    validationErrors: { type: Object, default: null },
    /** Selected icon has a canvas breakpoint (left rail). */
    hasBreakpoint: { type: Boolean, default: false },
    readonly: { type: Boolean, default: false },
    /** Advanced + Pinned multi-select note. Docked panel only. */
    banner: { type: String, default: '' },
};

function renderBreakpointRail(hasBreakpoint) {
    if (!hasBreakpoint)
        return null;
    return (
        <div
            class="c-FlowV2-icon-breakpoint-rail"
            title={BREAKPOINT_RAIL_TITLE}
            style={{
                position: 'absolute',
                left: 0,
                top: 0,
                bottom: 0,
                width: '8px',
                zIndex: 3,
            }}
        />
    );
}

function renderPropertyChrome(vm, { showClose }) {
    return (
        <div
            class="c-FlowV2-icon-overlay-chrome"
            style={{
                flex: '0 0 auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: '4px',
                padding: '4px 6px 0 6px',
                background: '#F8FAFC',
                borderBottom: '1px solid rgba(25, 103, 210, 0.12)',
            }}
        >
            <QCheckbox
                dense
                size="xs"
                modelValue={vm.advanced}
                onUpdate:modelValue={(v) => vm.$emit('update:advanced', !!v)}
                label="Advanced"
                style={{ fontSize: '11px', color: '#1967D2' }}
            />
            <QBtn
                flat
                dense
                round
                size="xs"
                color="primary"
                icon={vm.pinned ? 'mdi-pin' : 'mdi-pin-outline'}
                title={vm.pinned ? 'Unpin properties panel' : 'Pin properties to side panel'}
                onClick={() => vm.$emit('update:pinned', !vm.pinned)}
            />
            {showClose ? (
                <QBtn flat dense round size="xs" color="primary" icon="mdi-close"
                    title="Collapse"
                    onClick={() => vm.$emit('close')} />
            ) : null}
        </div>
    );
}

/** Compact banner for arg-level formatData.error on customForm / schemaObject editors. */
function renderCompilerErrorBanner(message) {
    if (!message || typeof message !== 'string')
        return null;
    return (
        <div
            class="c-FlowV2-icon-compiler-error"
            style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '6px',
                padding: '4px 6px',
                fontSize: '12px',
                color: 'var(--q-negative, #c10015)',
                lineHeight: 1.3,
            }}
        >
            <QIcon name="mdi-alert" size="xs" color="negative" style={{ marginTop: '2px', flexShrink: 0 }} />
            <span>{message}</span>
        </div>
    );
}

function wrapWithCompilerError(message, node, key) {
    const banner = renderCompilerErrorBanner(message);
    if (!banner)
        return key != null ? <div key={key}>{node}</div> : node;
    return (
        <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {banner}
            {node}
        </div>
    );
}

function renderEssentialEditorStack(vm, { finishRenderHandler, prefix }) {
    const args = vm.model?.Arguments;
    const argProps = vm.schema?.properties?.Arguments?.properties;
    const editors = listEssentialArgumentEditors(vm.tool, args, argProps);
    const matchForm = buildMatchPortsFormItems(vm.tool);
    const showMatchPorts = matchForm.length > 0 && vm.schema?.properties?.MatchPorts;
    const sel = vm.selectionKey || '';
    const nodes = [];

    const caption = getCaptionEditorDescriptor(vm.model, vm.schema);
    if (caption) {
        nodes.push(
            <SchemaForm
                key={`caption-${prefix}-${sel}`}
                name={`${prefix}Caption`}
                root={vm.root}
                schema={caption.schema}
                form={caption.form}
                cmodel={caption.cmodel}
                noinput={!!vm.readonly}
                IsAdvanced={false}
                autofocusFirst={false}
                enableVariableBinding={true}
                flowContext={vm.flowContext}
                parentType="VerticalStack"
                scopeitems={vm.scopeitems}
                controlscope={vm.controlscope}
                child={0}
            />
        );
    }

    for (const ed of editors) {
        // Object / custom forms have no single Quasar input for the arg; show arg-level error above.
        const argError = ed.kind === 'customForm'
            ? null // customForm banner computed inside branch with filtered Errors
            : getMixedStackArgCompilerError(ed.kind, argProps?.[ed.argName]);

        if (ed.kind === 'customForm') {
            const formErrors = errorsMapForCustomForm(vm.validationErrors, ed.argName);
            const bag = getCompileErrorBag(vm, ed.argName);
            syncCompileErrorErrorsMap(bag, formErrors);
            // Banner only for scalar / unpathable; nested leaves go to BasicForm via Errors map.
            nodes.push(
                wrapWithCompilerError(
                    getCustomFormCompilerErrorBanner(formErrors),
                    <UserControl
                        key={`form-${prefix}-${sel}-${ed.argName}-${vm.readonly ? 'ro' : 'rw'}`}
                        name={`${prefix}Form_${ed.argName}`}
                        root={vm.root}
                        controlURL={ed.formUrl}
                        controlName={`${prefix}Form_${ed.argName}`}
                        controlData={{
                            FormModel: ed.cmodel != null ? new PlainWrapper(ed.cmodel) : null,
                            CompileError: new PlainWrapper(bag),
                            ReadOnly: !!vm.readonly,
                            /** Inject-backed context; custom forms may also call these from controlData. */
                            VariableBinding: vm.variableBindingContext || null,
                            FlowContext: vm.flowContext || null,
                            $onUseVariable: (payload) => vm.variableBindingContext?.notifyUseVariable?.(payload),
                            $onCreateVariable: (name) => vm.variableBindingContext?.createVariable?.(
                                typeof name === 'string' ? name : name?.Name || name?.name,
                            ),
                        }}
                        scopeitems={vm.scopeitems}
                        controlscope={vm.controlscope}
                        onFinishedRender={(reference) => finishRenderHandler(reference)}
                    />,
                    `form-${prefix}-${sel}-${ed.argName}`,
                ),
            );
            continue;
        }
        nodes.push(
            wrapWithCompilerError(
                argError,
                <SchemaForm
                    name={`${prefix}Arg_${ed.argName}`}
                    root={vm.root}
                    schema={ed.schema}
                    form={ed.form || ['*']}
                    cmodel={ed.cmodel}
                    noinput={!!vm.readonly}
                    IsAdvanced={false}
                    autofocusFirst={false}
                    enableVariableBinding={true}
                    flowContext={vm.flowContext}
                    parentType="VerticalStack"
                    scopeitems={vm.scopeitems}
                    controlscope={vm.controlscope}
                    child={0}
                />,
                `arg-${prefix}-${sel}-${ed.argName}`,
            ),
        );
    }

    if (showMatchPorts) {
        nodes.push(
            <SchemaForm
                key={`match-${prefix}-${sel}`}
                name={`${prefix}MatchPorts`}
                root={vm.root}
                schema={vm.schema}
                form={matchForm}
                cmodel={vm.model}
                noinput={!!vm.readonly}
                IsAdvanced={false}
                autofocusFirst={false}
                enableVariableBinding={true}
                flowContext={vm.flowContext}
                parentType="VerticalStack"
                scopeitems={vm.scopeitems}
                controlscope={vm.controlscope}
                child={0}
            />
        );
    }

    const output = getOutputEditorDescriptor(vm.model, vm.schema);
    if (output) {
        nodes.push(
            <SchemaForm
                key={`output-${prefix}-${sel}`}
                name={`${prefix}Output`}
                root={vm.root}
                schema={output.schema}
                form={output.form}
                cmodel={output.cmodel}
                noinput={!!vm.readonly}
                IsAdvanced={false}
                autofocusFirst={false}
                enableVariableBinding={true}
                flowContext={vm.flowContext}
                parentType="VerticalStack"
                scopeitems={vm.scopeitems}
                controlscope={vm.controlscope}
                child={0}
            />
        );
    }

    if (!nodes.length)
        return <div style={{ padding: '8px', color: '#666', fontSize: '12px' }}>No properties</div>;

    return (
        <div class="c-FlowV2-icon-custom-form-stack" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {nodes}
        </div>
    );
}

function wrapWithFlowContext(vm, body) {
    if (!vm.flowContext)
        return body;
    return (
        <FlowContextProvider context={vm.flowContext}>
            {body}
        </FlowContextProvider>
    );
}

function wrapEssentialWithVariableBinding(vm, body) {
    if (vm.advanced || !vm.variableBindingContext)
        return body;
    if (typeof vm.variableBindingContext._setEnabled === 'function')
        vm.variableBindingContext._setEnabled(true);
    else
        vm.variableBindingContext.enabled = true;
    return (
        <VariableBindingProvider context={vm.variableBindingContext}>
            {body}
        </VariableBindingProvider>
    );
}

function renderPropertyBody(vm, { finishRenderHandler, contentScale }) {
    const prefix = vm.namePrefix || 'iconOverlay';
    let body;
    if (vm.advanced) {
        if (vm.variableBindingContext) {
            if (typeof vm.variableBindingContext._setEnabled === 'function')
                vm.variableBindingContext._setEnabled(false);
            else
                vm.variableBindingContext.enabled = false;
        }
        body = wrapWithFlowContext(
            vm,
            (
                <PropertyGrid
                    key={vm.selectionKey || `${prefix}Advanced`}
                    name={`${prefix}Advanced`}
                    root={vm.root}
                    schema={vm.schema}
                    form={[]}
                    cmodel={vm.model}
                    IsAdvanced={true}
                    readonly={!!vm.readonly}
                    flowContext={vm.flowContext}
                    parentType="VerticalStack"
                    scopeitems={vm.scopeitems}
                    controlscope={vm.controlscope}
                    child={0}
                />
            ),
        );
    }
    else if (shouldUseMixedEssentialStack(vm.tool) && vm.model?.Arguments != null) {
        body = wrapWithFlowContext(
            vm,
            wrapEssentialWithVariableBinding(
                vm,
                renderEssentialEditorStack(vm, { finishRenderHandler, prefix }),
            ),
        );
    }
    else if (vm.schema) {
        body = wrapWithFlowContext(
            vm,
            wrapEssentialWithVariableBinding(
                vm,
                (
                    <SchemaForm
                        name={`${prefix}Essential`}
                        root={vm.root}
                        schema={vm.schema}
                        form={buildEssentialIconForm(vm.schema, vm.tool)}
                        cmodel={vm.model}
                        noinput={!!vm.readonly}
                        IsAdvanced={false}
                        autofocusFirst={false}
                        enableVariableBinding={true}
                        flowContext={vm.flowContext}
                        parentType="VerticalStack"
                        scopeitems={vm.scopeitems}
                        controlscope={vm.controlscope}
                        child={0}
                    />
                ),
            ),
        );
    }
    else {
        body = <div style={{ padding: '8px', color: '#666', fontSize: '12px' }}>No properties</div>;
    }

    if (vm.readonly) {
        body = (
            <div style={{ position: 'relative' }}>
                {body}
                <div style={{ position: 'absolute', inset: 0, zIndex: 4 }} />
            </div>
        );
    }

    if (contentScale === 1)
        return body;

    return (
        <div
            ref="overlayContentScale"
            class="c-FlowV2-icon-overlay-content-scale"
            style={{
                transform: `scale(${contentScale})`,
                transformOrigin: 'top left',
                // Layout width so post-scale content still fills the body
                width: `${100 / contentScale}%`,
            }}
        >
            {body}
        </div>
    );
}

/**
 * Property form popup anchored next to a FlowV2 icon (does not resize the JointJS element).
 * Sized in paper-local units and scaled with `scale` so the popup tracks graph zoom.
 * Essential: auto height to content (capped by maxHeight); width resizable; form body at relative scale.
 * Advanced: PropertyGrid2; width and height resizable; full graph scale (no content downscale).
 * Ctrl/Cmd+wheel is captured and emitted as `zoom` (prevents browser zoom).
 */
export default defineComponent({
    name: 'flow-v2-icon-property-overlay',
    components: sharedFormComponents,
    props: {
        visible: { type: Boolean, default: false },
        left: { type: [Number, String], default: 0 },
        top: { type: [Number, String], default: 0 },
        /** Paper-local width (before zoom transform). */
        width: { type: Number, default: OVERLAY_WIDTH_PX },
        /** Paper-local Advanced height. Ignored for essential auto-size. */
        height: { type: Number, default: OVERLAY_HEIGHT_ADVANCED_PX },
        /** Cap for essential auto-height in paper-local px. */
        maxHeight: { type: Number, default: 400 },
        /** JointJS paper scale — applied via CSS transform so form UI matches graph zoom. */
        scale: { type: Number, default: 1 },
        /** Which side of the icon the overlay sits on — mirrors resize grips when 'left'. */
        placement: { type: String, default: 'right' },
        title: { type: String, default: '' },
        ...sharedFormProps,
    },
    emits: ['update:advanced', 'update:pinned', 'close', 'resize', 'contentHeight', 'zoom'],
    data: () => ({
        resizing: false,
        resizeStart: null,
        _measureScheduled: false,
    }),
    watch: {
        visible() { this.scheduleMeasure(); },
        advanced() { this.scheduleMeasure(); },
        schema: { deep: true, handler() { this.scheduleMeasure(); } },
        model: { deep: true, handler() { this.scheduleMeasure(); } },
        maxHeight() { this.scheduleMeasure(); },
        width() { this.scheduleMeasure(); },
        scale() { this.scheduleMeasure(); },
    },
    methods: {
        finishRenderHandler(reference) {
            if (this.$parent && typeof this.$parent.finishRenderHandler === 'function')
                this.$parent.finishRenderHandler(reference);
            this.scheduleMeasure();
        },
        onOverlayPointerDown(e) {
            // smooth-dnd listens for mousedown/touchstart on document (bubble).
            // Stopping those here blocks array reorder. pointerdown still stops
            // so the canvas does not treat the press as a paper hit.
            if (!shouldStopOverlayPress(e.type))
                return;
            e.stopPropagation();
        },
        onOverlayWheel(e) {
            // Capture Ctrl/Cmd+wheel so the browser does not zoom the page; parent zooms the graph.
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                e.stopPropagation();
                this.$emit('zoom', e);
            }
        },
        scheduleMeasure() {
            if (this.advanced || !this.visible || this._measureScheduled)
                return;
            this._measureScheduled = true;
            nextTick(() => {
                requestAnimationFrame(() => {
                    this._measureScheduled = false;
                    this.measureContentHeight();
                });
            });
        },
        measureContentHeight() {
            if (this.advanced || !this.visible)
                return;
            const root = this.$refs.overlayRoot;
            const body = this.$refs.overlayBody;
            if (!root || !body)
                return;
            const chrome = root.querySelector('.c-FlowV2-icon-overlay-chrome');
            const chromeH = chrome ? chrome.offsetHeight : 28;
            const cs = getComputedStyle(body);
            const padY = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);

            const scaleEl = this.$refs.overlayContentScale;
            let contentH;
            if (scaleEl) {
                // Transform does not shrink layout; collapse leftover space and size
                // the frame to the visual (scaled) content height.
                const layoutH = scaleEl.offsetHeight;
                const visualH = layoutH * ESSENTIAL_FORM_RELATIVE_SCALE;
                scaleEl.style.marginBottom = `${visualH - layoutH}px`;
                contentH = visualH + padY;
            }
            else {
                contentH = body.scrollHeight;
            }

            const natural = Math.max(OVERLAY_HEIGHT_ESSENTIAL_MIN_PX, chromeH + contentH);
            const capped = Math.min(natural, Math.max(OVERLAY_HEIGHT_ESSENTIAL_MIN_PX, this.maxHeight));
            this.$emit('contentHeight', capped);
        },
        /** @param {'ew'|'ns'|'se'|'sw'} mode */
        onResizePointerDown(e, mode) {
            if (mode === 'ns' && !this.advanced)
                return;
            if ((mode === 'se' || mode === 'sw') && !this.advanced)
                return;
            e.preventDefault();
            e.stopPropagation();
            this.resizing = true;
            this.resizeStart = {
                mode,
                x: e.clientX,
                y: e.clientY,
                width: this.width,
                height: this.height,
            };
            window.addEventListener('pointermove', this.onResizePointerMove);
            window.addEventListener('pointerup', this.onResizePointerUp);
        },
        onResizePointerMove(e) {
            if (!this.resizing || !this.resizeStart)
                return;
            const { mode, x, y, width, height } = this.resizeStart;
            const s = this.scale || 1;
            // Pointer delta is screen px; size props are paper-local (outer shell uses graph scale)
            const dx = (e.clientX - x) / s;
            const dy = (e.clientY - y) / s;
            const invertX = this.placement === 'left';
            let nextW = width;
            let nextH = height;
            if (mode === 'ew' || mode === 'se' || mode === 'sw')
                nextW = Math.max(OVERLAY_MIN_WIDTH_PX, Math.round(width + (invertX ? -dx : dx)));
            if ((mode === 'ns' || mode === 'se' || mode === 'sw') && this.advanced)
                nextH = Math.max(OVERLAY_MIN_HEIGHT, Math.round(height + dy));
            this.$emit('resize', { width: nextW, height: nextH });
        },
        onResizePointerUp() {
            this.resizing = false;
            this.resizeStart = null;
            window.removeEventListener('pointermove', this.onResizePointerMove);
            window.removeEventListener('pointerup', this.onResizePointerUp);
        },
    },
    mounted() {
        this.scheduleMeasure();
    },
    updated() {
        this.scheduleMeasure();
    },
    unmounted() {
        window.removeEventListener('pointermove', this.onResizePointerMove);
        window.removeEventListener('pointerup', this.onResizePointerUp);
    },
    render() {
        if (!this.visible || !this.model)
            return null;

        const left = typeof this.left === 'number' ? `${this.left}px` : this.left;
        const top = typeof this.top === 'number' ? `${this.top}px` : this.top;
        const useAutoHeight = !this.advanced;
        const s = this.scale || 1;
        const contentScale = this.advanced ? 1 : ESSENTIAL_FORM_RELATIVE_SCALE;
        const placeLeft = this.placement === 'left';

        const sizeStyle = useAutoHeight
            ? {
                width: `${this.width}px`,
                height: 'auto',
                maxHeight: `${this.maxHeight}px`,
            }
            : {
                width: `${this.width}px`,
                height: `${this.height}px`,
            };

        const widthHandleStyle = placeLeft
            ? {
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: this.advanced ? 10 : 0,
                width: '8px',
                cursor: 'ew-resize',
                background: 'linear-gradient(270deg, transparent 40%, rgba(66,133,244,0.35) 100%)',
            }
            : {
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: this.advanced ? 10 : 0,
                width: '8px',
                cursor: 'ew-resize',
                background: 'linear-gradient(90deg, transparent 40%, rgba(66,133,244,0.35) 100%)',
            };

        const nsHandleStyle = placeLeft
            ? {
                position: 'absolute',
                left: 10,
                right: 0,
                bottom: 0,
                height: '10px',
                cursor: 'ns-resize',
                background: 'linear-gradient(180deg, transparent 40%, rgba(66,133,244,0.35) 100%)',
            }
            : {
                position: 'absolute',
                left: 0,
                right: 10,
                bottom: 0,
                height: '10px',
                cursor: 'ns-resize',
                background: 'linear-gradient(180deg, transparent 40%, rgba(66,133,244,0.35) 100%)',
            };

        const cornerHandleStyle = placeLeft
            ? {
                position: 'absolute',
                left: 0,
                bottom: 0,
                width: '12px',
                height: '12px',
                cursor: 'nesw-resize',
                background: 'rgba(66,133,244,0.45)',
                borderRadius: '0 2px 0 4px',
            }
            : {
                position: 'absolute',
                right: 0,
                bottom: 0,
                width: '12px',
                height: '12px',
                cursor: 'nwse-resize',
                background: 'rgba(66,133,244,0.45)',
                borderRadius: '2px 0 4px 0',
            };

        return (
            <div
                ref="overlayRoot"
                class="c-FlowV2-icon-property-overlay"
                onMousedown={this.onOverlayPointerDown}
                onPointerdown={this.onOverlayPointerDown}
                onWheel={this.onOverlayWheel}
                style={{
                    position: 'absolute',
                    left,
                    top,
                    ...sizeStyle,
                    transform: s === 1 ? undefined : `scale(${s})`,
                    transformOrigin: 'top left',
                    zIndex: 2000,
                    display: 'flex',
                    flexDirection: 'column',
                    background: '#FFFFFF',
                    border: '1px solid rgba(25, 103, 210, 0.35)',
                    borderLeft: this.hasBreakpoint ? `4px solid ${BREAK_ACCENT_COLOR}` : undefined,
                    borderRadius: '8px',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                    overflow: 'hidden',
                    boxSizing: 'border-box',
                }}
            >
                {renderBreakpointRail(this.hasBreakpoint)}
                {renderPropertyChrome(this, { showClose: true })}
                <div
                    ref="overlayBody"
                    style={{
                        flex: useAutoHeight ? '0 1 auto' : '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '2px 8px 8px 8px',
                        background: '#FFFFFF',
                    }}
                >
                    {renderPropertyBody(this, {
                        finishRenderHandler: (r) => this.finishRenderHandler(r),
                        contentScale,
                    })}
                </div>
                {/* Width grip on the free edge (away from the icon) */}
                <div
                    title="Resize width"
                    onPointerdown={(e) => this.onResizePointerDown(e, 'ew')}
                    style={widthHandleStyle}
                />
                {this.advanced ? (
                    <>
                        <div
                            title="Resize height"
                            onPointerdown={(e) => this.onResizePointerDown(e, 'ns')}
                            style={nsHandleStyle}
                        />
                        <div
                            title="Resize"
                            onPointerdown={(e) => this.onResizePointerDown(e, placeLeft ? 'sw' : 'se')}
                            style={cornerHandleStyle}
                        />
                    </>
                ) : null}
            </div>
        );
    },
});

/**
 * Docked Essentials/Advanced editor for the lower-right Properties pane.
 * No graph zoom scale, absolute positioning, or resize handles.
 */
const IconPropertyDockedPanel = defineComponent({
    name: 'flow-v2-icon-property-docked',
    components: sharedFormComponents,
    props: {
        ...sharedFormProps,
        namePrefix: { type: String, default: 'iconDocked' },
        description: { type: String, default: '' },
    },
    emits: ['update:advanced', 'update:pinned'],
    methods: {
        finishRenderHandler(reference) {
            if (this.$parent && typeof this.$parent.finishRenderHandler === 'function')
                this.$parent.finishRenderHandler(reference);
        },
    },
    render() {
        if (!this.model) {
            return (
                <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden', position: 'relative' }}>
                    {renderPropertyChrome(this, { showClose: false })}
                    <div style={{ padding: '8px', fontSize: '12px', color: '#555' }}>
                        Select a flow icon to edit its properties.
                    </div>
                </div>
            );
        }

        return (
            <div
                class="c-FlowV2-icon-property-docked"
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    overflow: 'hidden',
                    background: '#FFFFFF',
                    boxSizing: 'border-box',
                    position: 'relative',
                    borderLeft: this.hasBreakpoint ? `4px solid ${BREAK_ACCENT_COLOR}` : undefined,
                }}
            >
                {renderBreakpointRail(this.hasBreakpoint)}
                {renderPropertyChrome(this, { showClose: false })}
                {this.banner ? (
                    <div style={{
                        flex: '0 0 auto',
                        padding: '6px 8px',
                        fontSize: '12px',
                        color: '#1967D2',
                        background: '#F8FAFC',
                        borderBottom: '1px solid rgba(25, 103, 210, 0.12)',
                    }}>
                        {this.banner}
                    </div>
                ) : null}
                <div
                    style={{
                        flex: '1 1 auto',
                        minHeight: 0,
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        padding: '2px 8px 8px 8px',
                        background: '#FFFFFF',
                    }}
                >
                    {renderPropertyBody(this, {
                        finishRenderHandler: (r) => this.finishRenderHandler(r),
                        contentScale: 1,
                    })}
                </div>
                {this.description ? (
                    <div style={{
                        flex: '0 0 auto',
                        borderTop: '2px solid gray',
                        minHeight: '60px',
                        padding: '8px',
                        fontSize: '12px',
                    }}>
                        {this.description}
                    </div>
                ) : null}
            </div>
        );
    },
});

export {
    IconPropertyDockedPanel,
    OVERLAY_WIDTH_PX,
    OVERLAY_MIN_WIDTH_PX,
    OVERLAY_HEIGHT_ESSENTIAL_MIN_PX,
    OVERLAY_HEIGHT_ADVANCED_PX,
    OVERLAY_MIN_HEIGHT,
    ESSENTIAL_FORM_RELATIVE_SCALE,
};
